import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import { initGA } from '../config/ga';
import RequireRole from '../components/auth/RequireRole';

const Login = lazy(() => import('../components/Login'));
const Register = lazy(() => import('../components/auth/Register'));
const ForgotPassword = lazy(() => import('../components/auth/ForgotPassword'));
const CheckEmail = lazy(() => import('../components/auth/CheckEmail'));
const ResetPassword = lazy(() => import('../components/auth/ResetPassword'));
const Dashboard = lazy(() => import('../components/DashBoard'));
const DashboardEngagementPage = lazy(
  () => import('../components/pages/DashboardEngagementPage'),
);
const StoresPage = lazy(() => import('../components/pages/StoresPage'));
const StoreFormPage = lazy(() => import('../components/pages/StoreFormPage'));
const ProductsPage = lazy(() => import('../components/pages/ProductsPage'));
const ProfessionalsPage = lazy(() => import('../components/pages/ProfessionalsPage'));
const ProfessionalFormPage = lazy(
  () => import('../components/pages/ProfessionalFormPage'),
);
const CitiesCategoriesPage = lazy(
  () => import('../components/pages/CitiesCategoriesPage'),
);
const BannersPage = lazy(() => import('../components/pages/BannersPage'));
const BannerFormPage = lazy(() => import('../components/pages/BannerFormPage'));
const UsersPage = lazy(() => import('../components/pages/UsersPage'));
const AuditLogsPage = lazy(() => import('../components/pages/AuditLogsPage'));
const OnlineUsersPage = lazy(
  () => import('../components/pages/OnlineUsersPage'),
);
const BillsPage = lazy(() => import('../components/pages/BillsPage'));
const MerchantProductFormPage = lazy(
  () => import('../components/pages/merchant/MerchantProductFormPage'),
);
const MerchantStoreSettingsPage = lazy(
  () => import('../components/pages/merchant/MerchantStoreSettingsPage'),
);
const PlansPage = lazy(() => import('../components/pages/PlansPage'));
const ReportsPage = lazy(() => import('../components/pages/ReportsPage'));
const ServicesListPage = lazy(
  () => import('../components/pages/services/ServicesListPage'),
);
const ServiceFormPage = lazy(
  () => import('../components/pages/services/ServiceFormPage'),
);
const ProfessionalSettingsPage = lazy(
  () => import('../components/pages/services/ProfessionalSettingsPage'),
);
const ModerationPage = lazy(
  () => import('../components/pages/ModerationPage'),
);
const PlaceholderPage = lazy(() => import('../components/PlaceholderPage'));

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { appUser, loading } = useAuth();
  if (loading) return <div className="login-page">Carregando...</div>;
  if (!localStorage.getItem('jwt_token') || !appUser) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function guarded(path: string, element: React.ReactNode) {
  return (
    <RequireAuth>
      <RequireRole path={path}>{element}</RequireRole>
    </RequireAuth>
  );
}

const AppRoutes: React.FC = () => {
  const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  React.useEffect(() => {
    if (gaMeasurementId) {
      initGA(gaMeasurementId);
    }
  }, [gaMeasurementId]);

  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<div className="login-page">Loading...</div>}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/cadastro" element={<Register />} />
            <Route path="/esqueci-senha" element={<ForgotPassword />} />
            <Route path="/verifique-email" element={<CheckEmail />} />
            <Route path="/redefinir-senha" element={<ResetPassword />} />
            <Route path="/dashboard" element={guarded('/dashboard', <Dashboard />)} />
            <Route
              path="/dashboard/engajamento"
              element={guarded('/dashboard', <DashboardEngagementPage />)}
            />
            <Route path="/lojas" element={guarded('/lojas', <StoresPage />)} />
            <Route path="/lojas/nova" element={guarded('/lojas', <StoreFormPage />)} />
            <Route
              path="/lojas/:id/editar"
              element={guarded('/lojas', <StoreFormPage />)}
            />
            <Route path="/produtos" element={guarded('/produtos', <ProductsPage />)} />
            <Route
              path="/produtos/novo"
              element={guarded('/produtos', <MerchantProductFormPage />)}
            />
            <Route
              path="/produtos/:id/editar"
              element={guarded('/produtos', <MerchantProductFormPage />)}
            />
            <Route
              path="/profissionais"
              element={guarded('/profissionais', <ProfessionalsPage />)}
            />
            <Route
              path="/profissionais/novo"
              element={guarded('/profissionais', <ProfessionalFormPage />)}
            />
            <Route
              path="/profissionais/:id/editar"
              element={guarded('/profissionais', <ProfessionalFormPage />)}
            />
            <Route
              path="/cidades"
              element={guarded('/cidades', <CitiesCategoriesPage />)}
            />
            <Route path="/banners" element={guarded('/banners', <BannersPage />)} />
            <Route
              path="/banners/novo"
              element={guarded('/banners', <BannerFormPage />)}
            />
            <Route
              path="/banners/:id/editar"
              element={guarded('/banners', <BannerFormPage />)}
            />
            <Route path="/usuarios" element={guarded('/usuarios', <UsersPage />)} />
            <Route
              path="/auditoria"
              element={guarded('/auditoria', <AuditLogsPage />)}
            />
            <Route
              path="/auditoria/online"
              element={guarded('/auditoria', <OnlineUsersPage />)}
            />
            <Route
              path="/moderacao"
              element={guarded('/moderacao', <ModerationPage />)}
            />
            <Route
              path="/relatorios"
              element={guarded('/relatorios', <ReportsPage />)}
            />
            <Route path="/planos" element={guarded('/planos', <PlansPage />)} />
            <Route path="/boletos" element={guarded('/boletos', <BillsPage />)} />
            <Route
              path="/servicos"
              element={guarded('/servicos', <ServicesListPage />)}
            />
            <Route
              path="/servicos/novo"
              element={guarded('/servicos', <ServiceFormPage />)}
            />
            <Route
              path="/servicos/:id/editar"
              element={guarded('/servicos', <ServiceFormPage />)}
            />
            <Route
              path="/meu-perfil"
              element={guarded('/meu-perfil', <ProfessionalSettingsPage />)}
            />
            <Route
              path="/configuracoes"
              element={guarded('/configuracoes', <PlaceholderPage />)}
            />
            <Route
              path="/minha-loja"
              element={guarded('/minha-loja', <MerchantStoreSettingsPage />)}
            />
            <Route path="/equipe" element={guarded('/equipe', <PlaceholderPage />)} />
            <Route
              path="/whatsapp"
              element={guarded('/whatsapp', <PlaceholderPage />)}
            />
            <Route path="/" element={<Login />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default AppRoutes;
